import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

export type ChannelType = "webhook" | "ntfy" | "email" | "telegram";
export type NtfyAuthType = "none" | "token" | "password";

export interface AlertChannelFormState {
  name: string;
  channelType: ChannelType;
  enabled: boolean;
  webhookUrl: string;
  ntfyTopic: string;
  ntfyServer: string;
  ntfyAuthType: NtfyAuthType;
  ntfyUser: string;
  ntfyPass: string;
  ntfyBearerToken: string;
  emailRecipients: string;
  telegramToken: string;
  telegramChatId: string;
}

export interface ExistingConfig {
  bearer_token_set?: boolean;
  password_set?: boolean;
  bot_token_set?: boolean;
}

export interface AlertChannelFormProps {
  mode: "create" | "edit";
  state: AlertChannelFormState;
  onChange: (updates: Partial<AlertChannelFormState>) => void;
  existingConfig?: ExistingConfig;
  smtpConfigured?: boolean;
  error: string | null;
  onErrorDismiss: () => void;
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
  testResult?: { ok: boolean; message?: string } | null;
  onTestResultDismiss?: () => void;
  onTest?: () => void;
  testing?: boolean;
}

export function AlertChannelForm({
  mode,
  state,
  onChange,
  existingConfig = {},
  smtpConfigured = false,
  error,
  onErrorDismiss,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
  testResult,
  onTestResultDismiss,
  onTest,
  testing = false,
}: AlertChannelFormProps) {
  const { channelType } = state;
  const channelTypeLocked = mode === "edit";

  // Keep label shrunk (focused style) so placeholder "(unchanged)" is readable
  const secretFieldLabelProps = { InputLabelProps: { shrink: true } };

  return (
    <form onSubmit={onSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onErrorDismiss}>
          {error}
        </Alert>
      )}
      {testResult && (
        <Alert
          severity={testResult.ok ? "success" : "error"}
          sx={{ mb: 2 }}
          onClose={onTestResultDismiss}
        >
          {testResult.ok ? "Test notification sent" : testResult.message}
        </Alert>
      )}

      <TextField
        size="small"
        label="Name"
        value={state.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={mode === "create" ? "e.g. Slack #alerts" : undefined}
        fullWidth
        sx={{ mb: 2 }}
      />

      {channelTypeLocked ? (
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
          Type: {channelType}
        </Typography>
      ) : (
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={channelType}
            label="Type"
            onChange={(e) => onChange({ channelType: e.target.value as ChannelType })}
          >
            <MenuItem value="webhook">Webhook</MenuItem>
            <MenuItem value="ntfy">ntfy</MenuItem>
            <MenuItem value="email" disabled={!smtpConfigured}>
              Email {!smtpConfigured && "(SMTP not configured)"}
            </MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
          </Select>
        </FormControl>
      )}

      {channelType === "webhook" && (
        <TextField
          size="small"
          label="URL"
          value={state.webhookUrl}
          onChange={(e) => onChange({ webhookUrl: e.target.value })}
          placeholder="https://..."
          fullWidth
          required
          sx={{ mb: 2 }}
        />
      )}

      {channelType === "ntfy" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Topic"
            value={state.ntfyTopic}
            onChange={(e) => onChange({ ntfyTopic: e.target.value })}
            placeholder="my-alerts"
            fullWidth
            required
          />
          <TextField
            size="small"
            label="Server (optional)"
            value={state.ntfyServer}
            onChange={(e) => onChange({ ntfyServer: e.target.value })}
            placeholder="https://ntfy.sh"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Authentication</InputLabel>
            <Select
              value={state.ntfyAuthType}
              label="Authentication"
              onChange={(e) => {
                const v = e.target.value as NtfyAuthType;
                onChange({
                  ntfyAuthType: v,
                  ntfyBearerToken: v !== "token" ? "" : state.ntfyBearerToken,
                  ntfyUser: v !== "password" ? "" : state.ntfyUser,
                  ntfyPass: v !== "password" ? "" : state.ntfyPass,
                });
              }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="token">Bearer token</MenuItem>
              <MenuItem value="password">Username & password</MenuItem>
            </Select>
          </FormControl>
          {state.ntfyAuthType === "token" && (
            <TextField
              size="small"
              type="password"
              label="Bearer token"
              value={state.ntfyBearerToken}
              onChange={(e) => onChange({ ntfyBearerToken: e.target.value })}
              placeholder={
                existingConfig.bearer_token_set
                  ? "(unchanged)"
                  : mode === "edit"
                    ? "Leave blank to keep current"
                    : "tk_..."
              }
              fullWidth
              {...(mode === "edit" ? secretFieldLabelProps : {})}
            />
          )}
          {state.ntfyAuthType === "password" && (
            <>
              <TextField
                size="small"
                label="Username"
                value={state.ntfyUser}
                onChange={(e) => onChange({ ntfyUser: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                type="password"
                label="Password"
                value={state.ntfyPass}
                onChange={(e) => onChange({ ntfyPass: e.target.value })}
                placeholder={
                  existingConfig.password_set
                    ? "(unchanged)"
                    : mode === "edit"
                      ? "Leave blank to keep current"
                      : undefined
                }
                fullWidth
                {...(mode === "edit" ? secretFieldLabelProps : {})}
              />
            </>
          )}
        </Box>
      )}

      {channelType === "email" && (
        <TextField
          size="small"
          label="Recipients (comma-separated)"
          value={state.emailRecipients}
          onChange={(e) => onChange({ emailRecipients: e.target.value })}
          placeholder="a@example.com, b@example.com"
          fullWidth
          required
          sx={{ mb: 2 }}
        />
      )}

      {channelType === "telegram" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            type="password"
            label="Bot token"
            value={state.telegramToken}
            onChange={(e) => onChange({ telegramToken: e.target.value })}
            placeholder={
              existingConfig.bot_token_set
                ? "•••••••• (unchanged)"
                : mode === "edit"
                  ? "Leave blank to keep current"
                  : undefined
            }
            fullWidth
            required={mode === "create"}
            {...(mode === "edit" ? secretFieldLabelProps : {})}
          />
          <TextField
            size="small"
            label="Chat ID"
            value={state.telegramChatId}
            onChange={(e) => onChange({ telegramChatId: e.target.value })}
            fullWidth
            required
          />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Switch
          checked={state.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        <Typography variant="body2">Enabled</Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="contained" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </Button>
        {onTest && (
          <Button
            variant="outlined"
            onClick={onTest}
            disabled={testing || (channelType === "email" && !smtpConfigured)}
          >
            {testing ? "Sending…" : "Test"}
          </Button>
        )}
        <Button variant="text" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </Box>
    </form>
  );
}
